<!-- eslint-disable vue/no-parsing-error -->
<!-- FleetDashboard.vue -->
<template>
  <v-container fluid>
    <v-card elevation="2" class="rounded-lg">
  <v-card-title class="pa-6 pb-0">
    <div class="d-flex flex-column flex-sm-row align-center justify-space-between w-100">
      <div class="d-flex align-center mb-3 mb-sm-0">
        <span class="text-h5 font-weight-medium primary--text">
          Fleet Dashboard
        </span>
        <v-icon
          size="32"
          color="primary"
          class="ml-3"
        >
          mdi-view-dashboard
        </v-icon>
      </div>
    </div>
  </v-card-title>

  <v-divider class="mx-4 mt-4"></v-divider>

  <v-card-subtitle class="pa-6 pt-4">
    <div class="d-flex flex-column flex-sm-row align-center justify-space-between">
      <!-- Date Range -->
      <div class="d-flex align-center mb-3 mb-sm-0">
        <v-icon
          size="20"
          color="grey darken-1"
          class="mr-2"
        >
          mdi-calendar-range
        </v-icon>
        <span class="text-subtitle-1 font-weight-medium grey--text text--darken-2">
          {{ startOfPreviousWeek }} — {{ endOfPreviousWeek }}
        </span>
      </div>

      <!-- Navigation Controls -->
      <div class="d-flex align-center">
        <v-btn
          icon
          small
          color="primary"
          class="mr-2"
          @click="incrementWeek"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <div class="px-4 py-1 rounded-lg grey lighten-4">
          <span class="text-body-2 font-weight-medium grey--text text--darken-3">
            Week <span class="primary--text font-weight-bold">{{ week }}</span>
          </span>
        </div>

        <v-btn
          icon
          small
          color="primary"
          class="ml-2"
          @click="decrementWeek"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>
  </v-card-subtitle>

      <!-- KPI Cards with Change Indicator Rings -->
      <v-container>
        <v-row dense>

          <!-- KPI 0: Total Vehicles  -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fas fa-car</v-icon>
                  Active Vehicles</div>
                <RingChart
                  :percentage-change="vehiclesInactivePercentage"
                  :lower-is-better="true"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h5 font-weight-black"> {{ totalVehicles - vehiclesOldUpdate }} / {{ totalVehicles }} </div>
                    <div class="text-caption grey--text">vehicles</div>
                  </div>
                </RingChart>

                <div class="mt-2">
                  <v-chip
                    :color="vehiclesInactivePercentage <= 0 ? 'success' : 'error'"
                    small
                  >
                    <v-icon left small>{{ vehiclesInactivePercentage <= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                    {{ Math.abs(vehiclesInactivePercentage) }}% of Fleet Inactive
                  </v-chip>

                  <div class="text-caption grey--text mt-1">
                    {{ vehiclesOldUpdate }} Inactive Vehicle
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- KPI 1: Total Trips (higher is better) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fas fa-route</v-icon>
                  Total Trips</div>
                <RingChart
                  :percentage-change="tripsChangePercentage"
                  :lower-is-better="false"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h4 font-weight-black">{{ totalTrips }}</div>
                    <div class="text-caption grey--text">trips</div>
                  </div>
                </RingChart>

                <div class="mt-2">
                  <v-chip
                    :color="tripsChangePercentage <= 0 ? 'warning' : 'info'"
                    small
                  >
                    <v-icon left small>{{ tripsChangePercentage >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                    {{ Math.abs(tripsChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekTrips }} trips
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- KPI 2: Total Violations (lower is better) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fa-solid fa-triangle-exclamation</v-icon>
                  Total Violations</div>
                <RingChart
                  :percentage-change="violationsChangePercentage"
                  :lower-is-better="true"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h4 font-weight-black">{{ totalViolations }}</div>
                    <div class="text-caption grey--text">violations</div>
                  </div>
                </RingChart>
                <div class="mt-2">
                  <v-chip
                    :color="violationsChangePercentage <= 0 ? 'success' : 'error'"
                    small
                  >
                    <v-icon left small>{{ violationsChangePercentage <= 0 ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                    {{ Math.abs(violationsChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekViolations }} violations
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- KPI 3: Total Duration (neutral - just showing trend) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fa-stopwatch</v-icon>
                  Total Duration</div>
                <RingChart
                  :percentage-change="durationChangePercentage"
                  :lower-is-better="false"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h6 font-weight-black">{{ totalDuration }}</div>
                    <div class="text-caption grey--text">drive time</div>
                  </div>
                </RingChart>
                <div class="mt-2">
                  <v-chip
                    :color="durationChangePercentage >= 0 ? 'warning' : 'info'"
                    small
                  >
                    <v-icon left small>{{ durationChangePercentage >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                    {{ Math.abs(durationChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekDuration }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

        </v-row>

        <!-- Additional row for more KPIs -->
        <v-row dense>
          <!-- KPI 4: Total Distance (neutral - just showing trend) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fa-solid fa-ruler-horizontal</v-icon>
                  Total Distance</div>
                <RingChart
                  :percentage-change="distanceChangePercentage"
                  :lower-is-better="false"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h6 font-weight-black">{{ totalDistance }}</div>
                    <div class="text-caption grey--text">kilometers</div>
                  </div>
                </RingChart>
                <div class="mt-2">
                  <v-chip
                    :color="distanceChangePercentage >= 0 ? 'warning' : 'info'"
                    small
                  >
                    <v-icon left small>{{ distanceChangePercentage >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                    {{ Math.abs(distanceChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekDistance }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- KPI 5: Average Speed (lower is better for safety) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">Average Speed</div>
                <RingChart
                  :percentage-change="avgSpeedChangePercentage"
                  :lower-is-better="true"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h5 font-weight-black">{{ avgSpeed }}</div>
                    <div class="text-caption grey--text">km/h</div>
                  </div>
                </RingChart>
                <div class="mt-2">
                  <v-chip
                    :color="avgSpeedChangePercentage <= 0 ? 'success' : 'error'"
                    small
                  >
                    <v-icon left small>{{ avgSpeedChangePercentage <= 0 ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                    {{ Math.abs(avgSpeedChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekAvgSpeed }} km/h
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- KPI 6: Idle Time (lower is better) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fa-solid fa-hourglass-half</v-icon>
                  Idle Time</div>
                <RingChart
                  :percentage-change="idleTimeChangePercentage"
                  :lower-is-better="true"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h6 font-weight-black">{{ totalIdleTime }}</div>
                    <div class="text-caption grey--text">idle duration</div>
                  </div>
                </RingChart>
                <div class="mt-2">
                  <v-chip
                    :color="idleTimeChangePercentage <= 0 ? 'success' : 'error'"
                    small
                  >
                    <v-icon left small>{{ idleTimeChangePercentage <= 0 ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                    {{ Math.abs(idleTimeChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekIdleTime }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- KPI 7: Speed Time (higher might indicate aggressive driving, so lower is better) -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 kpi-card" elevation="2">
              <div class="text-center">
                <div class="text-caption font-weight-medium">
                  <v-icon left small>fa-solid fa-gauge-high</v-icon>
                  Speed Time</div>
                <RingChart
                  :percentage-change="speedTimeChangePercentage"
                  :lower-is-better="true"
                  :size="140"
                  thickness="70%"
                >
                  <div>
                    <div class="text-h6 font-weight-black">{{ totalSpeedTime }}</div>
                    <div class="text-caption grey--text">above speed limit</div>
                  </div>
                </RingChart>
                <div class="mt-2">
                  <v-chip
                    :color="speedTimeChangePercentage <= 0 ? 'success' : 'error'"
                    small
                  >
                    <v-icon left small>{{ speedTimeChangePercentage <= 0 ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                    {{ Math.abs(speedTimeChangePercentage) }}% vs last week
                  </v-chip>
                  <div class="text-caption grey--text mt-1">
                    Previous: {{ previousWeekSpeedTime }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

<!--       <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            @click="toggleMobileView"
            :color="forceMobileView ? 'primary' : 'default'"
          >
            <v-icon>{{ forceMobileView ? 'mdi-table-off' : 'mdi-table' }}</v-icon>
          </v-btn>
        </div>

      </v-card-title>
 -->
      <!-- Data Grid Component
        @open-day="handleOpenDay"
        @open-nav="handleOpenNav"

      -->
      <FleetDataGridMain

        :mobile-breakpoint="0"
        :enable-mobile-toggle="true"
        :use-mobile-cards="false"

        :rows="dataRowsTotal"
        :loading="loading"
        :current-week="week"
        :base-url="$baseUrl"
        title="Fleet Performance Details"

        @fetch-details="handleTotalExpanded"

        @row-expanded="handleTotalRowExpanded"
      />

<!--
    <v-btn color="primary" @click="showGrid2 = !showGrid2">
      {{ showGrid2 ? 'Hide' : 'Show' }} Grid
    </v-btn>
 -->
        <!-- Card -->
    <v-card
      ref="cardTarget"
      v-if="showGrid2"
      class="mt-4"
      outlined
    >

<!--             <v-card-title class="d-flex justify-space-between align-center">
              <div class="d-flex align-center">
                <v-btn
                  icon
                  small
                  @click="toggleMobileView2"
                  :color="forceMobileView2 ? 'primary' : 'default'"
                >
                  <v-icon>{{ forceMobileView2 ? 'mdi-table-off' : 'mdi-table' }}</v-icon>
                </v-btn>
              </div>

            </v-card-title>
 -->
            <!-- Data Grid Component -->
            <FleetDataGrid
              :mobile-breakpoint="0"
              :enable-mobile-toggle="true"
              :use-mobile-cards="false"

              :rows="dataRows"
              :loading="loading"
              :current-week="week"
              :base-url="$baseUrl"
              title="Vehicle Performance Details"
              @open-day="handleOpenDay"
              @open-nav="handleOpenNav"
              @fetch-details="handleFetchDetails"

              @row-expanded="handleRowExpanded"
            />

          </v-card>

    </v-card>

  </v-container>
</template>

<script>
// eslint-disable no-unused-expressions

import RingChart from '@/components/RingChart.vue'
import FleetDataGrid from './components/FleetDataGrid.vue'
import FleetDataGridMain from './components/FleetDataGridMain.vue'

export default {
  name: 'FleetDashboard',
  components: {
    RingChart,
    FleetDataGrid,
    FleetDataGridMain
  },

  data () {
    return {
      totalVehicles: 0,
      vehiclesOldUpdate: 0,
      vehiclesLowBattery: 0,
      //
      selectedBreakpoint2: 'auto',
      forceMobileView2: false,
      //
      showGrid2: false,
      //
      selectedBreakpoint: 'auto',
      forceMobileView: false,
      useMobileCards: false,
      //
      week: 0,
      loading: false,
      rows: []
    }
  },

  mounted () {
    // this.loadData()
    this.fetchAndProcessVehicles()
    this.loadSavedBreakpointPreference()
    this.loadData()
  },

  computed: {

    activeMobileBreakpoint () {
      if (this.forceMobileView) {
        return 9999 // Force mobile view
      }

      switch (this.selectedBreakpoint) {
      case 'always-table':
        return 0 // Always show table
      case 'always-mobile':
        return 9999 // Always show mobile
      case 'auto':
      default:
        return 600 // Standard breakpoint
      }
    },

    activeMobileBreakpoint2 () {
      if (this.forceMobileView2) {
        return 9999 // Force mobile view
      }

      switch (this.selectedBreakpoint2) {
      case 'always-table':
        return 0 // Always show table
      case 'always-mobile':
        return 9999 // Always show mobile
      case 'auto':
      default:
        return 600 // Standard breakpoint
      }
    },
    //
    startOfPreviousWeek () {
      const addDays = this.week * 7
      const pastDate = new Date()
      pastDate.setDate(pastDate.getDate() - addDays)
      const year = pastDate.getFullYear()
      const month = String(pastDate.getMonth() + 1).padStart(2, '0')
      const day = String(pastDate.getDate()).padStart(2, '0')
      const input = `${year}-${month}-${day}`
      const date = new Date(input)
      const dayOfWeek = date.getDay()
      const diff = (7 + (dayOfWeek - 1)) % 7
      const startOfWeek = new Date(date)
      startOfWeek.setDate(startOfWeek.getDate() - diff)
      const startOfPreviousWeek = new Date(startOfWeek)
      startOfPreviousWeek.setDate(startOfPreviousWeek.getDate() - 7)
      return this.formatDate(startOfPreviousWeek)
    },

    endOfPreviousWeek () {
      const addDays = this.week * 7
      const pastDate = new Date()
      pastDate.setDate(pastDate.getDate() - addDays)
      const year = pastDate.getFullYear()
      const month = String(pastDate.getMonth() + 1).padStart(2, '0')
      const day = String(pastDate.getDate()).padStart(2, '0')
      const input = `${year}-${month}-${day}`
      const date = new Date(input)
      const dayOfWeek = date.getDay()
      const diff = (7 + (dayOfWeek - 1)) % 7
      const startOfWeek = new Date(date)
      startOfWeek.setDate(startOfWeek.getDate() - diff)
      const startOfPreviousWeek = new Date(startOfWeek)
      startOfPreviousWeek.setDate(startOfPreviousWeek.getDate() - 7)
      const endOfPreviousWeek = new Date(startOfWeek)
      endOfPreviousWeek.setDate(endOfPreviousWeek.getDate() - 1)
      return this.formatDate(endOfPreviousWeek)
    },

    dataRows () {
      return this.rows.filter(r => r.unitId !== 'TOTAL')
    },

    dataRowsTotal () {
      return this.rows.filter(r => r.unitId === 'TOTAL')
    },

    // Current week values
    totalTrips () {
      return this.dataRows.reduce((s, r) => s + r.trips, 0)
    },

    totalViolations () {
      return this.dataRows.reduce((s, r) => s + r.violations, 0)
    },

    totalDuration () {
      return this.formatSeconds(
        this.dataRows.reduce((s, r) => s + r.durationSeconds, 0)
      )
    },

    totalDurationSeconds () {
      return this.dataRows.reduce((s, r) => s + r.durationSeconds, 0)
    },

    totalDistance () {
      return this.formatMeters(
        this.dataRows.reduce((s, r) => s + r.distanceMeters, 0)
      )
    },

    totalDistanceMeters () {
      return this.dataRows.reduce((s, r) => s + r.distanceMeters, 0)
    },

    totalIdleTime () {
      return this.formatSeconds(
        this.dataRows.reduce((s, r) => s + (r.idleSeconds || 0), 0)
      )
    },

    totalIdleSeconds () {
      return this.dataRows.reduce((s, r) => s + (r.idleSeconds || 0), 0)
    },

    totalSpeedTime () {
      return this.formatSeconds(
        this.dataRows.reduce((s, r) => s + (r.speedTimeSeconds || 0), 0)
      )
    },

    totalSpeedTimeSeconds () {
      return this.dataRows.reduce((s, r) => s + (r.speedTimeSeconds || 0), 0)
    },

    avgSpeed () {
      const totalDistanceKm = this.totalDistanceMeters / 1000
      const totalHours = this.totalDurationSeconds / 3600
      if (totalHours === 0) return 0
      return (totalDistanceKm / totalHours).toFixed(1)
    },

    // Previous week values (from the delta data)
    previousWeekTrips () {
      const prev = this.totalTrips - this.totalDTrips
      return Math.max(0, prev)
    },

    previousWeekViolations () {
      const prev = this.totalViolations - this.totalDViolations
      return Math.max(0, prev)
    },

    previousWeekDuration () {
      const prevSeconds = this.totalDurationSeconds - this.totalDDurationSeconds
      return this.formatSeconds(Math.max(0, prevSeconds))
    },

    previousWeekDurationSeconds () {
      return Math.max(0, this.totalDurationSeconds - this.totalDDurationSeconds)
    },

    previousWeekDistance () {
      const prevMeters = this.totalDistanceMeters - this.totalDDistanceMeters
      return this.formatMeters(Math.max(0, prevMeters))
    },

    previousWeekDistanceMeters () {
      return Math.max(0, this.totalDistanceMeters - this.totalDDistanceMeters)
    },

    previousWeekIdleTime () {
      const prevSeconds = this.totalIdleSeconds - this.totalDIdleSeconds
      return this.formatSeconds(Math.max(0, prevSeconds))
    },

    previousWeekIdleSeconds () {
      return Math.max(0, this.totalIdleSeconds - this.totalDIdleSeconds)
    },

    previousWeekSpeedTime () {
      const prevSeconds = this.totalSpeedTimeSeconds - this.totalDSpeedTimeSeconds
      return this.formatSeconds(Math.max(0, prevSeconds))
    },

    previousWeekSpeedTimeSeconds () {
      return Math.max(0, this.totalSpeedTimeSeconds - this.totalDSpeedTimeSeconds)
    },

    previousWeekAvgSpeed () {
      const prevDistanceKm = this.previousWeekDistanceMeters / 1000
      const prevHours = this.previousWeekDurationSeconds / 3600
      if (prevHours === 0) return 0
      return (prevDistanceKm / prevHours).toFixed(1)
    },

    // Delta values (change from previous week)
    totalDTrips () {
      return this.dataRows.reduce((s, r) => s + r.dTrips, 0)
    },

    totalDViolations () {
      return this.dataRows.reduce((s, r) => s + r.dViolations, 0)
    },

    totalDDurationSeconds () {
      return this.dataRows.reduce((s, r) => {
        if (!r.dDuration) return s
        const match = r.dDuration.match(/([+-])(\d{2}):(\d{2})/)
        if (!match) return s
        const sign = match[1] === '+' ? 1 : -1
        const hours = parseInt(match[2])
        const minutes = parseInt(match[3])
        const seconds = (hours * 3600) + (minutes * 60)
        return s + (sign * seconds)
      }, 0)
    },

    /// Modified to handle cases where strings with space padded + or - e.g '+ 23.00' or '- 42.9'
    // dDistance might be missing or not in expected format
    totalDDistanceMeters () {
      return this.dataRows.reduce((s, r) => {
        if (!r.dDistance) return s

        const match = r.dDistance.match(/([+-])\s*(\d+(?:\.\d+)?)/)

        if (!match) return s

        const sign = match[1] === '+' ? 1 : -1
        const km = parseFloat(match[2])

        return s + (sign * km * 1000)
      }, 0)
    },

    totalDDistanceMeters_v1 () {
      return this.dataRows.reduce((s, r) => {
        if (!r.dDistance) return s
        const match = r.dDistance.match(/([+-])(\d+(?:\.\d+)?)/)
        if (!match) return s
        const sign = match[1] === '+' ? 1 : -1
        const km = parseFloat(match[2])
        return s + (sign * km * 1000)
      }, 0)
    },

    totalDIdleSeconds () {
      return this.dataRows.reduce((s, r) => {
        if (!r.dIdle) return s
        const match = r.dIdle.match(/([+-])(\d{2}):(\d{2})/)
        if (!match) return s
        const sign = match[1] === '+' ? 1 : -1
        const hours = parseInt(match[2])
        const minutes = parseInt(match[3])
        const seconds = (hours * 3600) + (minutes * 60)
        return s + (sign * seconds)
      }, 0)
    },

    totalDSpeedTimeSeconds () {
      return this.dataRows.reduce((s, r) => {
        if (!r.dSpeedTime) return s
        const match = r.dSpeedTime.match(/([+-])(\d{2}):(\d{2})/)
        if (!match) return s
        const sign = match[1] === '+' ? 1 : -1
        const hours = parseInt(match[2])
        const minutes = parseInt(match[3])
        const seconds = (hours * 3600) + (minutes * 60)
        return s + (sign * seconds)
      }, 0)
    },

    // Percentage changes
    vehiclesInactivePercentage () {
      if (this.vehiclesOldUpdate === 0) return 0

      return parseFloat(((this.vehiclesOldUpdate / this.totalVehicles) * 100).toFixed(1))
    },

    tripsChangePercentage () {
      if (this.previousWeekTrips === 0) return 0
      return parseFloat(((this.totalDTrips / this.previousWeekTrips) * 100).toFixed(1))
    },

    violationsChangePercentage () {
      if (this.previousWeekViolations === 0) return 0
      return parseFloat(((this.totalDViolations / this.previousWeekViolations) * 100).toFixed(1))
    },

    durationChangePercentage () {
      if (this.previousWeekDurationSeconds === 0) return 0
      return parseFloat(((this.totalDDurationSeconds / this.previousWeekDurationSeconds) * 100).toFixed(1))
    },

    distanceChangePercentage () {
      if (this.previousWeekDistanceMeters === 0) return 0
      return parseFloat(((this.totalDDistanceMeters / this.previousWeekDistanceMeters) * 100).toFixed(1))
    },

    idleTimeChangePercentage () {
      if (this.previousWeekIdleSeconds === 0) return 0
      return parseFloat(((this.totalDIdleSeconds / this.previousWeekIdleSeconds) * 100).toFixed(1))
    },

    speedTimeChangePercentage () {
      if (this.previousWeekSpeedTimeSeconds === 0) return 0
      return parseFloat(((this.totalDSpeedTimeSeconds / this.previousWeekSpeedTimeSeconds) * 100).toFixed(1))
    },

    avgSpeedChangePercentage () {
      const current = parseFloat(this.avgSpeed)
      const previous = parseFloat(this.previousWeekAvgSpeed)
      if (previous === 0) return 0
      const change = current - previous
      return parseFloat(((change / previous) * 100).toFixed(1))
    }

  },

  methods: {

    async fetchAndProcessVehicles () {
      try {
        const url = this.$baseUrl + '/api/v2/vehicle'
        const response = await this.$axiosCache.get(url)
        const vehicles = response.data

        // Calculate statistics
        const oneWeekAgo = new Date()
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

        const totalVehicles = vehicles.length
        let oldLastUpdateCount = 0
        let lowBatteryCount = 0

        vehicles.forEach(vehicle => {
        // Check if LastUpdate is older than a week
          const lastUpdateDate = new Date(vehicle.LastUpdate)
          if (lastUpdateDate < oneWeekAgo) {
            oldLastUpdateCount++
          }

          // Check if battery is under 12V
          if (vehicle.CarBattery < 12) {
            lowBatteryCount++
          }
        })

        // Use the results
        console.log(`Total vehicles: ${totalVehicles}`)
        console.log(`Vehicles with LastUpdate older than a week: ${oldLastUpdateCount}`)
        console.log(`Vehicles with battery under 12V: ${lowBatteryCount}`)

        // You can also store these values in your component's data
        this.totalVehicles = totalVehicles
        this.vehiclesOldUpdate = oldLastUpdateCount
        this.vehiclesLowBattery = lowBatteryCount

        return {
          totalVehicles,
          oldLastUpdateCount,
          lowBatteryCount
        }
      } catch (error) {
        console.error('Error fetching vehicle data:', error)
        // Handle error appropriately
        this.error = 'Failed to fetch vehicle data'
      }
    },

    handleTotalExpanded ({ expanded }) {
      console.log('Total row expanded:', expanded)

      this.showGrid2 = !this.showGrid2

      this.$nextTick(() => {
        this.$vuetify.goTo(this.$refs.cardTarget, {
          duration: 500,
          offset: 20,
          easing: 'easeInOutCubic'
        })
      })
    },

    handleBreakpointChange (value) {
      console.log('Breakpoint changed to:', value)
      this.forceMobileView = false

      // Optionally save preference to localStorage
      localStorage.setItem('fleetGridBreakpoint', value)
    },

    toggleMobileView () {
      this.forceMobileView = !this.forceMobileView
      this.selectedBreakpoint = 'auto' // Reset selection when toggling manually

      // Show notification
      if (this.forceMobileView) {
        // eslint-disable-next-line no-unused-expressions
        this.$toast?.info('Mobile view enabled - showing cards instead of table')
      } else {
        // eslint-disable-next-line no-unused-expressions
        this.$toast?.info('Table view restored')
      }
    },

    toggleMobileView2 () {
      this.forceMobileView2 = !this.forceMobileView2
      this.selectedBreakpoint2 = 'auto' // Reset selection when toggling manually

      // Show notification
      if (this.forceMobileView) {
        // eslint-disable-next-line no-unused-expressions
        this.$toast?.info('Mobile view enabled - showing cards instead of table')
      } else {
        // eslint-disable-next-line no-unused-expressions
        this.$toast?.info('Table view restored')
      }
    },

    handleMobileViewToggled (isMobile) {
      console.log('Mobile view toggled:', isMobile)
      // You could add analytics tracking here
    },

    // Load saved preference on mount
    loadSavedBreakpointPreference () {
      const saved = localStorage.getItem('fleetGridBreakpoint')
      if (saved && ['always-table', 'auto', 'always-mobile'].includes(saved)) {
        this.selectedBreakpoint = saved
      }
    },

    // Programmatically control the grid
    setGridView () {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.dataGrid?.setMobileBreakpoint(0)
    },

    setMobileView () {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.dataGrid?.setMobileBreakpoint(9999)
    },

    toggleTableView () {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.dataGrid?.toggleTableView()
    },

    //
    handleOpenDay ({ detailItem, parentItem }) {
      const log = `/trip-list-speed/${parentItem.unitId}/${parentItem.RegNumber}/${this.formatDate_yyyyMMdd(new Date(detailItem.Item1))}`
      console.log('Opening day:', log)
      this.$router.push({ path: log })
    },

    handleOpenNav ({ subItem, parentItem }) {
      const log = `/day-trip-navigator/${parentItem.unitId}/${parentItem.RegNumber}/summary/${this.formatDate_yyyyMMdd(new Date(subItem.Item1))}/0`
      console.log('Opening navigator:', log)
      this.$router.push({ path: log })
    },

    async handleFetchDetails ({ item, week }) {
      item.detailsLoading = true
      try {
        const url = this.$baseUrl + `/api/v2/summary/by-day?UnitID=${item.unitId}&Week=${week}`
        const response = await this.$axios.get(url)
        item.details = response.data
      } catch (e) {
        console.error('Error loading details', e)
        item.details = []
      } finally {
        item.detailsLoading = false
      }
    },

    handleTotalRowExpanded ({ item, expanded }) {
      console.log(`Row ${item} expanded:`, expanded)
    },

    handleRowExpanded ({ item, expanded }) {
      console.log(`Row ${item.unitId} expanded:`, expanded)
    },

    incrementWeek () {
      this.week++
      this.loadData()
    },

    decrementWeek () {
      if (this.week > 0) {
        this.week--
        this.loadData()
      }
    },

    formatDate (date) {
      const d = new Date(date)
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const dayAbbr = dayNames[d.getDay()]
      const dayOfMonth = String(d.getDate()).padStart(2, '0')
      const monthAbbr = monthNames[d.getMonth()]
      return `${dayAbbr} ${dayOfMonth}, ${monthAbbr}`
    },

    formatDate_yyyyMMdd (d) {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    async loadData () {
      this.loading = true
      try {
        let url = this.$baseUrl
        url += `/api/v2/summary?Week=${this.week}`
        const response = await this.$axios.get(url)
        this.rows = response.data.map(r => ({
          unitId: r[0],
          RegNumber: r[1] || '',
          trips: r[2],
          dTrips: r[3],
          violations: r[4],
          dViolations: r[5],
          duration: r[6],
          dDuration: r[7],
          distance: r[8],
          dDistance: r[9],
          idle: r[10],
          dIdle: r[11],
          speedTime: r[12],
          dSpeedTime: r[13],
          durationSeconds: r[14],
          distanceMeters: r[15],
          idleSeconds: this.parseDurationToSeconds(r[10]),
          speedTimeSeconds: this.parseDurationToSeconds(r[12]),
          details: null,
          detailsLoading: false
        }))
      } catch (err) {
        console.error('Error loading summary:', err)
      } finally {
        this.loading = false
      }
    },

    parseDurationToSeconds (durationStr) {
      if (!durationStr) return 0
      const match = durationStr.match(/(\d{2}):(\d{2})/)
      if (!match) return 0
      const hours = parseInt(match[1])
      const minutes = parseInt(match[2])
      return (hours * 3600) + (minutes * 60)
    },

    formatSeconds (sec) {
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      return `${h}h ${m}m`
    },

    formatMeters (m) {
      return (m / 1000).toFixed(1) + ' km'
    }
  }
}
</script>

<style scoped>
.kpi-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Small text styling for previous week values */
.caption.mt-1 {
  font-size: 0.7rem;
  opacity: 0.8;
}
</style>
